import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import Layout from "@/components/Layout";
import Feed from "@/pages/Feed";
import CreatePost from "@/pages/CreatePost";
import PostDetail from "@/pages/PostDetail";
import Messages from "@/pages/Messages";
import Notifications from "@/pages/Notifications";
import Profile from "@/pages/Profile";
import PublicProfile from "@/pages/PublicProfile";
import Admin from "@/pages/Admin";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import Donations from "@/pages/Donations";
import Plans from "@/pages/Plans";
import Family from "@/pages/Family";
import Communities from "@/pages/Communities";
import TownPage from "@/pages/TownPage";
import PaymentResult from "@/pages/PaymentResult";
import { HeartHandshake } from "lucide-react";

function FullLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-cream">
      <div className="w-16 h-16 rounded-full bg-terracotta flex items-center justify-center animate-pulse">
        <HeartHandshake className="text-white" size={32} strokeWidth={2.5} />
      </div>
    </div>
  );
}

function Protected({ children, adminOnly }) {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/app" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (user) return <Navigate to="/app" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
      <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/payment/success" element={<PaymentResult mode="success" />} />
      <Route path="/payment/cancel" element={<PaymentResult mode="cancel" />} />
      <Route path="/app" element={<Protected><Layout /></Protected>}>
        <Route index element={<Feed />} />
        <Route path="new" element={<CreatePost />} />
        <Route path="post/:id" element={<PostDetail />} />
        <Route path="messages" element={<Messages />} />
        <Route path="messages/:cid" element={<Messages />} />
        <Route path="donations" element={<Donations />} />
        <Route path="plans" element={<Plans />} />
        <Route path="family" element={<Family />} />
        <Route path="communities" element={<Communities />} />
        <Route path="communities/:id" element={<TownPage />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="profile" element={<Profile />} />
        <Route path="members/:id" element={<PublicProfile />} />
        <Route path="admin" element={<Protected adminOnly><Admin /></Protected>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-center" richColors toastOptions={{ style: { fontSize: "16px" } }} />
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
